import { useEffect, useState } from "react";
import api from "../lib/api";
import ContentRail from "../components/ContentRail";
import CreatorHeader from "../components/CreatorHeader";
import { usePlayer } from "../context/PlayerContext";
import { Play, Sparkles, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

export default function Home() {
  const [feed, setFeed] = useState(null);
  const player = usePlayer();

  useEffect(() => { api.get("/videos/home").then((r) => setFeed(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <CreatorHeader />
      {!feed ? (
        <div className="min-h-[40vh] flex items-center justify-center text-zinc-500">Loading…</div>
      ) : (
        <HomeFeed feed={feed} player={player} />
      )}
    </div>
  );
}

// The big banner at the top of Home. With one featured video it just shows it. With several it moves on to the next one every
// 7 seconds (in the order set under Featured Videos in Admin), and the dots let a visitor pick one. It stops moving while a
// visitor points at it or has a button in it selected, and never moves by itself for anyone who asked their device for less motion.
const BANNER_MS = 7000;
function FeaturedBanner({ items, player }) {
  const [i, setI] = useState(0);
  const [hold, setHold] = useState(false);
  const n = items.length;
  const at = n ? Math.min(i, n - 1) : 0;
  const still = typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    if (n < 2 || hold || still) return undefined;
    const t = setTimeout(() => setI((x) => (x + 1) % n), BANNER_MS);
    return () => clearTimeout(t);
  }, [i, n, hold, still]);
  if (!n) return null;
  const hero = items[at];
  return (
    <section className="relative rounded-3xl overflow-hidden mb-10 border border-white/10" data-testid="hero-section"
      onPointerEnter={() => setHold(true)} onPointerLeave={() => setHold(false)} onFocus={() => setHold(true)} onBlur={() => setHold(false)}>
      {items.map((v, k) => (
        <img key={v.id} src={v.cover_url} alt="" aria-hidden="true" loading={k === 0 ? "eager" : "lazy"}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
          style={{ opacity: k === at ? 0.4 : 0 }} />
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0E] via-[#0A0A0E]/70 to-transparent" />
      <div className="relative p-6 sm:p-10 lg:p-14 min-h-[380px] flex flex-col justify-end" key={hero.id}>
        <span className="font-mono-ui text-xs uppercase tracking-widest text-cyan-400 mb-3 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Dan The Man&apos;s Featured Drop
        </span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-3 max-w-2xl">
          <span className="animated-gradient-text">{hero.title}</span>
        </h1>
        <p className="text-zinc-300 max-w-xl mb-6">{hero.description}</p>
        <div className="flex flex-wrap gap-3">
          <button data-testid="hero-play-btn" onClick={() => player.playOne(hero)}
            className="px-6 py-3 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 transition-colors flex items-center gap-2">
            <Play className="w-4 h-4" /> Play now
          </button>
          <Link to={`/watch/${hero.id}`} className="px-6 py-3 rounded-full border border-white/20 hover:bg-white/5 font-medium">
            View details
          </Link>
        </div>
        {n > 1 && (
          <div className="flex flex-wrap items-center gap-1 mt-6" role="group" aria-label="Featured videos" data-testid="hero-dots">
            {items.map((v, k) => (
              <button key={v.id} type="button" onClick={() => setI(k)} aria-label={`Show featured video ${k + 1} of ${n}: ${v.title}`}
                aria-current={k === at ? "true" : undefined} data-testid={`hero-dot-${k}`}
                className="w-6 h-6 flex items-center justify-center rounded-full">
                <span className={`block rounded-full transition-all ${k === at ? "w-3 h-3 bg-cyan-400" : "w-2 h-2 bg-white/40 hover:bg-white/70"}`} />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function HomeFeed({ feed, player }) {
  return (
    <div>
      <FeaturedBanner items={feed.featured || []} player={player} />

      <ContentRail testId="rail-featured" title="Featured Videos" videos={feed.featured} href="/videos?featured=true" />
      <ContentRail testId="rail-new" title="New Releases" videos={feed.new_releases} href="/videos?sort=recent" />
      <ContentRail testId="rail-trending" title={<span className="flex items-center gap-2"><TrendingUp className="w-6 h-6 text-crimson" /> Trending Now</span>} videos={feed.trending} href="/videos?sort=trending" />
      <ContentRail testId="rail-popular" title="Popular Music" videos={feed.popular} href="/videos?sort=popular" />
      <ContentRail testId="rail-dan-picks" title="Dan The Man's Picks" videos={feed.dan_picks} href="/videos?artist=Dan+The+Man" />
      {feed.genre_rows.map((row) => (
        <ContentRail key={row.slug} testId={`rail-genre-${row.slug}`} title={row.label} videos={row.videos} href={`/genres/${row.slug}`} />
      ))}
    </div>
  );
}